"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { LottieRefCurrentProps } from "lottie-react"
import dynamic from "next/dynamic"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/navigation"
import {
  transitionDelayTime,
  transitionTime,
  TransitionUp,
  useInViewConfig,
} from "ui"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import { delayTime, transitionEase } from "ui/transitions/utils"
import {
  extractProperty,
  getNonUmbracoUrl,
  infoClick,
  Link,
  pageStripFaq,
} from "utils"
import AccordionItemComp from "../../Common/FaqItem"
import { VideoControlButton, videoControls } from "../Hero/VideoControls"
import lottieAnimation from "./lottie.json"

const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const OuterContainer = styled.div<{ $isGray?: boolean }>`
  width: 100%;
  background-color: ${({ $isGray }) =>
    $isGray ? sharedColors.mediumGrayLight : "transparent"};
  position: relative;
`

const StyledLayout = styled.div`
  width: 100%;
  display: flex;
  gap: 82px;
  position: relative;
`

const ContentLayout = styled.div`
  padding: 64px 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 88px 0;
  }
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
`

const StyledLottie = styled(Lottie)`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
    height: fit-content;
  }
`

const AccordionArray = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  max-width: 1040px;
  gap: 24px;
`

const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 300px;
  }
`

const StyledTitleTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$inView",
})<{ $inView: boolean }>`
  opacity: ${(p) => (p.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inView ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
`

const ControlWrapper = styled.div`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
    position: absolute;
    top: 80px;
    right: 280px;
  }
`
type UndStr = string | undefined

type Item = {
  question: string
  content: string
  defaultExpanded: boolean
  buttonLink?: string
  buttonText?: string
}

type Props = {
  data: any
  blockIndex?: number
  isHomepage?: boolean
}

export default function FAQ({ data, blockIndex, isHomepage }: Props) {
  const title = extractProperty(data, "title") as UndStr
  const description = extractProperty(data, "description") as UndStr
  const fAQSearchLine = extractProperty(data, "fAQSearchLine") as UndStr
  const button = extractProperty(data, "button")
  const buttonText = button?.[0]?.name as UndStr
  const buttonLink = button?.[0]?.url as UndStr
  const itemsListBlock = extractProperty(data, "itemsList") || []
  const itemsList = itemsListBlock.map((el: any) => ({
    question: extractProperty(el, "question"),
    content: extractProperty(el, "answer"),
    buttonLink: extractProperty(el, "button")?.[0]?.url,
    buttonText: extractProperty(el, "button")?.[0]?.name,
  })) as Item[]

  const [expandedIndex, setExpandedIndex] = useState<number>(-1)
  const lottieRef = useRef<LottieRefCurrentProps>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const accordionRefs = useRef<(HTMLDivElement | null)[]>([])
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null)
  const handleChange =
    (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedIndex(isExpanded ? index : -1)
      infoClick(
        itemsList[index]?.question || "",
        isExpanded ? "פתיחה" : "סגירה",
        "שאלות נפוצות",
        "שאלות דף הבית",
      )
      if (isExpanded) {
        // Clear any existing timer
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current)
        }
        scrollTimerRef.current = setTimeout(() => {
          accordionRefs.current[index]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          })
          scrollTimerRef.current = null
        }, 300)
      }
    }
  const handleAnimationControl = () => {
    if (lottieRef.current) {
      if (isPlaying) {
        lottieRef.current.pause()
      } else {
        lottieRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const { ref, inView } = useInView(useInViewConfig)
  const isGray =
    isHomepage && typeof blockIndex === "number" && blockIndex % 2 === 1

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current)
      }
    }
  }, [])

  return (
    <OuterContainer ref={ref} $isGray={isGray}>
      <Container>
        <StyledLayout>
          <ContentLayout>
            <StyledTitleTypography variant="h2" $inView={inView}>
              {title}
            </StyledTitleTypography>

            <AccordionArray>
              {itemsList.map((item, i) => (
                <TransitionUp
                  $inView={inView}
                  $delay={transitionDelayTime * 0.5 * i}
                  key={i}
                >
                  <AccordionItemComp
                    ref={(el) => {
                      accordionRefs.current[i] = el
                    }}
                    key={i}
                    content={item.content}
                    title={item.question}
                    buttonLink={item.buttonLink}
                    buttonText={item.buttonText}
                    expanded={expandedIndex === i}
                    onChange={handleChange(i)}
                  />
                </TransitionUp>
              ))}
              <StyledButton
                variant="primary"
                size="medium"
                LinkComponent={Link}
                onClick={() => {
                  pageStripFaq(buttonText || "", "homepage", title || "")
                }}
                href={getNonUmbracoUrl(buttonLink)}
              >
                {buttonText}
              </StyledButton>
            </AccordionArray>
          </ContentLayout>
          <StyledLottie
            animationData={lottieAnimation}
            lottieRef={lottieRef}
            autoplay={true}
            loop={true}
          />
          <ControlWrapper>
            <VideoControlButton
              icon={
                isPlaying
                  ? videoControls.desktop.pause
                  : videoControls.desktop.play
              }
              onClick={handleAnimationControl}
              ariaLabel={isPlaying ? "Pause" : "Play"}
              size={24}
            />
          </ControlWrapper>
        </StyledLayout>
      </Container>
    </OuterContainer>
  )
}
